import React, { useState, useRef } from "react";
import { Send, CheckCircle, Phone, Clock, ShieldCheck, Sparkles, AlertCircle, Loader2, X, Camera, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { useQuoteModal } from "@/hooks/useQuoteModal";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";

const VIBE_API_URL = "https://backend.leadconnectorhq.com/vibe-ai";
const LOCATION_ID = "xfaKqeYCXXqhpM9mQvnB";

// Custom field IDs (registered for this location)
const JUNK_TYPE_FIELD_ID = "uBAN1vfzbHXQwkSd2sBW";
const JOB_SIZE_FIELD_ID = "Bn2hIuZP8hG5zp4m31qu";
const PREFERRED_TIME_FIELD_ID = "az98HgavEO1QtIdV0cC2";
const JUNK_PHOTOS_FIELD_ID = "qYPBaCU0aNBGXs0IDJmp";
const MAX_PHOTOS = 5;

export const QuoteModal: React.FC = () => {
  const { isOpen, closeModal } = useQuoteModal();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    junkType: "",
    jobSize: "",
    preferredTime: "",
    details: ""
  });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = MAX_PHOTOS - photos.length;
    const toAdd = files.slice(0, remaining);
    if (toAdd.length < files.length) {
      setSubmitError(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} can be added (max ${MAX_PHOTOS}).`);
    } else {
      setSubmitError("");
    }
    const newPhotos = [...photos, ...toAdd];
    setPhotos(newPhotos);
    const newPreviews = toAdd.map((f) => URL.createObjectURL(f));
    setPhotoPreviews((prev) => [...prev, ...newPreviews]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotos(photos.filter((_, i) => i !== index));
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index));
    setSubmitError("");
  };

  const handleClose = () => {
    closeModal();
    if (submitted) {
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          fullName: "", phone: "", email: "", address: "",
          junkType: "", jobSize: "", preferredTime: "", details: ""
        });
        photoPreviews.forEach((p) => URL.revokeObjectURL(p));
        setPhotos([]);
        setPhotoPreviews([]);
      }, 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    try {
      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Build custom fields for the contact
      const customFields: { id: string; field_value: string }[] = [];
      if (formData.junkType) {
        customFields.push({ id: JUNK_TYPE_FIELD_ID, field_value: formData.junkType });
      }
      if (formData.jobSize) {
        customFields.push({ id: JOB_SIZE_FIELD_ID, field_value: formData.jobSize });
      }
      if (formData.preferredTime) {
        customFields.push({ id: PREFERRED_TIME_FIELD_ID, field_value: formData.preferredTime });
      }

      // Build notes from optional fields
      const noteParts: string[] = [];
      if (formData.details) noteParts.push(`Details: ${formData.details}`);
      if (formData.address) noteParts.push(`Address: ${formData.address}`);

      let contactCreated = false;

      // METHOD 1: Create contact directly via CRM API (this creates the actual contact)
      try {
        // Send JSON payload for contact upsert
        const contactData = {
          locationId: LOCATION_ID,
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          address1: formData.address || undefined,
          customFields: customFields.length > 0 ? customFields : undefined,
          notes: noteParts.length > 0 ? noteParts.join(" | ") : undefined,
          tags: ["Website Quote Request", "Davie Junk Removal Lead"],
        };

        const contactResponse = await fetch(`${VIBE_API_URL}/contacts/upsert`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactData),
        });

        if (contactResponse.ok) {
          const contactResult = await contactResponse.json().catch(() => ({}));
          console.log("[QuoteForm] Contact created:", contactResult.contact?.id || "success");
          contactCreated = true;
        } else {
          const errText = await contactResponse.text().catch(() => "");
          console.error("[QuoteForm] Contact creation failed", contactResponse.status, errText);
        }
      } catch (contactErr) {
        console.warn("[QuoteForm] Contact API error:", contactErr);
      }

      // METHOD 2: Fire tracking event (analytics + secondary capture) — this is the working CRM connection
      let trackingSuccess = false;
      try {
        const tracking = buildTrackingPayload("quote-form", "Free Quote Form");
        tracking.formData = {
          first_name: firstName,
          last_name: lastName,
          email: formData.email,
          phone: formData.phone,
          address1: formData.address,
        };
        tracking.formLabels = {
          first_name: "First Name",
          last_name: "Last Name",
          email: "Email Address",
          phone: "Phone Number",
          address1: "Service Address",
        };
        trackingSuccess = await postTrackingEvent(tracking, {
          customFields: {
            [JUNK_TYPE_FIELD_ID]: { value: formData.junkType, label: "Type of Junk" },
            [JOB_SIZE_FIELD_ID]: { value: formData.jobSize, label: "Estimated Job Size" },
            [PREFERRED_TIME_FIELD_ID]: { value: formData.preferredTime, label: "Preferred Time" },
          },
          fileFields: photos.length > 0 ? {
            [JUNK_PHOTOS_FIELD_ID]: { files: photos, label: "Junk Photos" },
          } : undefined,
        });
        console.log("[QuoteForm] Tracking event result:", trackingSuccess);
      } catch (trackingErr) {
        console.warn("[QuoteForm] Tracking event error:", trackingErr);
      }

      // Success if either method worked
      if (!contactCreated && !trackingSuccess) {
        throw new Error("Both submission methods failed");
      }

      setSubmitted(true);
    } catch (err) {
      console.error("[QuoteForm] Submission error:", err);
      setSubmitError("Could not submit. Please try again or call (954) 280-8090.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white p-0 sm:rounded-3xl">
        <DialogTitle className="sr-only">Free Quote Form</DialogTitle>

        {/* Header band */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-rose-600 to-rose-700 text-white px-6 py-4 flex items-center justify-between sm:rounded-t-3xl">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-300" />
            <span className="font-black uppercase tracking-wide text-sm">Instant Free Davie Quote</span>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6">
          {submitted ? (
            <div className="bg-emerald-50 border-2 border-emerald-400 rounded-2xl p-8 text-center animate-in zoom-in-95 duration-300">
              <div className="w-16 h-16 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black font-heading text-slate-900 mb-2">
                Quote Request Received!
              </h3>
              <p className="text-slate-700 text-base max-w-md mx-auto mb-6">
                Thanks, <strong className="text-slate-950">{formData.fullName || "Friend"}</strong>! Our Davie Florida team is reviewing your job specs. Expect a call or text at <strong className="text-primary">{formData.phone || "your phone"}</strong> within 60 minutes.
              </p>
              <Button
                onClick={handleClose}
                className="bg-primary hover:bg-rose-700 text-white font-extrabold uppercase"
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-black font-heading text-slate-950 uppercase tracking-tight mb-1">
                  Get Your Free, <span className="text-primary">No-Obligation</span> Quote
                </h2>
                <p className="text-slate-600 text-sm">
                  Fill out the fast form below and our Davie haulers will get back to you within the hour — guaranteed!
                </p>
              </div>

              <form id="quote-form" name="Free Quote Form" onSubmit={handleSubmit} className="space-y-5">
                {/* Row 1: Name & Phone */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-black uppercase text-slate-700">Full Name *</Label>
                    <Input
                      required
                      name="first_name"
                      placeholder="e.g. Maria Rodriguez"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="h-12 border-slate-300 focus-visible:ring-primary text-slate-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-black uppercase text-slate-700">Phone Number *</Label>
                    <Input
                      required
                      type="tel"
                      name="phone"
                      placeholder="(954) 280-8090"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12 border-slate-300 focus-visible:ring-primary text-slate-900"
                    />
                  </div>
                </div>

                {/* Row 2: Email & Service Address */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-black uppercase text-slate-700">Email Address *</Label>
                    <Input
                      required
                      type="email"
                      name="email"
                      placeholder="maria@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12 border-slate-300 focus-visible:ring-primary text-slate-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-black uppercase text-slate-700">Service Address in Davie / Broward *</Label>
                    <Input
                      required
                      name="address1"
                      placeholder="e.g. 7300 Stirling Rd, Davie FL 33314"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="h-12 border-slate-300 focus-visible:ring-primary text-slate-900"
                    />
                  </div>
                </div>

                {/* Row 3: Junk Type & Estimated Size */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="junk-type-select" className="text-xs font-black uppercase text-slate-700">Type of Junk *</Label>
                    <Select
                      value={formData.junkType}
                      onValueChange={(val) => setFormData({ ...formData, junkType: val })}
                    >
                      <SelectTrigger id="junk-type-select" className="h-12 border-slate-300 text-slate-900">
                        <SelectValue placeholder="Choose junk type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Furniture">Furniture & Couches</SelectItem>
                        <SelectItem value="Appliances">Appliances (Fridge, Washer, Dryer)</SelectItem>
                        <SelectItem value="Full Cleanout">Full Property / Garage Cleanout</SelectItem>
                        <SelectItem value="Construction Debris">Construction & Renovation Debris</SelectItem>
                        <SelectItem value="Yard Waste">Yard Waste & Tree Limbs</SelectItem>
                        <SelectItem value="Electronics">Electronics & Tech E-Waste</SelectItem>
                        <SelectItem value="Commercial">Commercial Office Removal</SelectItem>
                        <SelectItem value="Other">Other / Mixed Trash</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-size-select" className="text-xs font-black uppercase text-slate-700">Estimated Job Size *</Label>
                    <Select
                      value={formData.jobSize}
                      onValueChange={(val) => setFormData({ ...formData, jobSize: val })}
                    >
                      <SelectTrigger id="job-size-select" className="h-12 border-slate-300 text-slate-900">
                        <SelectValue placeholder="Choose job size..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single Item">Single Heavy Item (e.g. Couch, Fridge)</SelectItem>
                        <SelectItem value="Few Items">Few Items (1/4 Truckload)</SelectItem>
                        <SelectItem value="Half Truckload">Half Truckload</SelectItem>
                        <SelectItem value="Full Truckload">Full Truckload</SelectItem>
                        <SelectItem value="Multiple Truckloads">Multiple Truckloads (Large Cleanout)</SelectItem>
                        <SelectItem value="Not Sure">Not Sure — Need On-Site Quote</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 4: Preferred Date / Details */}
                <div className="space-y-2">
                  <Label className="text-xs font-black uppercase text-slate-700">Preferred Date & Time Window</Label>
                  <Input
                    placeholder="e.g. Today afternoon or Friday morning"
                    value={formData.preferredTime}
                    onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                    className="h-12 border-slate-300 text-slate-900"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-black uppercase text-slate-700">Additional Details / Item Description</Label>
                  <Textarea
                    placeholder="Describe items, stairs, parking access or any specific instructions..."
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    className="min-h-[90px] border-slate-300 text-slate-900"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-3">
                  <Label className="text-xs font-black uppercase text-slate-700 flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5 text-amber-500" /> Snap Photos of Your Junk (Up to 5)
                  </Label>
                  <p className="text-xs text-slate-500 -mt-1">Helps us give you the most accurate quote — JPEG or PNG, max 5 photos.</p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif"
                    multiple
                    onChange={handlePhotoChange}
                    className="hidden"
                  />

                  {photoPreviews.length > 0 && (
                    <div className="grid grid-cols-5 gap-2">
                      {photoPreviews.map((src, i) => (
                        <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-slate-200">
                          <img src={src} alt={`Junk photo ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            className="absolute top-1 right-1 w-5 h-5 bg-rose-600 text-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove photo"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-slate-300 rounded-xl py-6 flex flex-col items-center gap-2 text-slate-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50/50 transition-colors"
                    >
                      <Camera className="w-7 h-7" />
                      <span className="text-sm font-bold">{photos.length === 0 ? "Add Photos" : `Add More (${photos.length}/${MAX_PHOTOS})`}</span>
                      <span className="text-xs text-slate-400">Tap to upload from your device</span>
                    </button>
                  )}
                </div>

                {/* Security indicator */}
                <div className="bg-slate-100 p-3 rounded-xl border border-slate-200 flex items-center gap-3 text-xs text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Verified Human Form Protection • SSL Encrypted Security</span>
                </div>

                {submitError && (
                  <div className="flex items-center gap-2 text-rose-600 text-sm bg-rose-50 border border-rose-200 rounded-xl p-3">
                    <AlertCircle className="w-4 h-4 shrink-0" /> {submitError}
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full h-14 bg-primary hover:bg-rose-700 text-white font-black text-lg uppercase tracking-wider shadow-xl shadow-rose-600/25 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Get My Free Davie Quote</>
                  )}
                </Button>

                <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-slate-500 font-bold pt-1">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" /> No commitment required
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-amber-500" /> Response within 60 minutes
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4 text-primary" /> Or call (954) 280-8090
                  </span>
                </div>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
